import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import api from '../utils/api';
import TestBotFolder from '../components/TestBotFolder';
import {
  ArrowLeft, Users, Bot, TrendingUp, Shield,
  RefreshCw, Loader2, Copy, UserPlus,
  Key, Trash2, Check, Activity, ChevronDown, ChevronUp,
  Wifi, WifiOff, Clock, ArrowUpDown, Search, FlaskConical
} from 'lucide-react';

const TIER_OPTIONS = [
  { value: 'core', label: 'Core', color: 'bg-green-500', desc: '1 bot, paper trading only' },
  { value: 'pro', label: 'Pro', color: 'bg-blue-500', desc: '3 bots, live trading' },
  { value: 'elite', label: 'Elite', color: 'bg-yellow-500', desc: '6 bots, all features' },
  { value: 'full', label: 'Full Access', color: 'bg-purple-500', desc: 'Unrestricted access (same as owner, no admin)' },
];

const AdminPanel = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [stats, setStats] = useState(null);
  const [modEmail, setModEmail] = useState('');
  const [modTier, setModTier] = useState('elite');
  const [mods, setMods] = useState([]);
  const [modCodes, setModCodes] = useState([]);
  const [modActivity, setModActivity] = useState([]);
  const [generatingCode, setGeneratingCode] = useState(false);
  const [codeTier, setCodeTier] = useState('core');
  const [copiedCode, setCopiedCode] = useState(null);
  const [expandedMod, setExpandedMod] = useState(null);
  const [userActivity, setUserActivity] = useState([]);
  const [activitySort, setActivitySort] = useState('last_login');
  const [activitySearch, setActivitySearch] = useState('');
  const [loadingActivity, setLoadingActivity] = useState(false);

  useEffect(() => {
    checkAdminAndLoad();
  }, []);

  const checkAdminAndLoad = async () => {
    setLoading(true);
    try {
      const adminCheck = await api.get('/admin/check');
      if (!adminCheck.data.is_admin) {
        toast.error('Admin access required');
        navigate('/dashboard');
        return;
      }
      setIsAdmin(true);
      await loadData();
    } catch (error) {
      toast.error('Admin access required');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadData = async () => {
    try {
      const [statsRes, modsRes, codesRes, activityRes, userActivityRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/mods'),
        api.get('/admin/mod-codes'),
        api.get('/admin/mod-activity'),
        api.get(`/admin/user-activity?sort_by=${activitySort}`),
      ]);
      setStats(statsRes.data);
      setMods(modsRes.data.mods || []);
      setModCodes(codesRes.data.codes || []);
      setModActivity(activityRes.data.activity || []);
      setUserActivity(userActivityRes.data.users || []);
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  const loadUserActivity = async () => {
    setLoadingActivity(true);
    try {
      const res = await api.get(`/admin/user-activity?sort_by=${activitySort}`);
      setUserActivity(res.data.users || []);
    } catch (error) {
      console.error('Failed to load user activity');
    } finally {
      setLoadingActivity(false);
    }
  };

  const makeMod = async () => {
    if (!modEmail.trim()) { toast.error('Enter an email address'); return; }
    try {
      await api.post(`/admin/make-mod?email=${encodeURIComponent(modEmail)}&tier=${modTier}`);
      toast.success(`${modEmail} is now a moderator with ${modTier} tier!`);
      setModEmail('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to make moderator');
    }
  };

  const revokeMod = async (email) => {
    if (!window.confirm(`Remove moderator access from ${email}?\n(Their account will be kept but downgraded to free tier)`)) return;
    try {
      await api.post(`/admin/revoke-mod?email=${encodeURIComponent(email)}`);
      toast.success(`${email} mod access removed`);
      loadData();
    } catch (error) {
      toast.error('Failed to revoke moderator');
    }
  };

  const deleteUser = async (email) => {
    if (!window.confirm(`PERMANENTLY DELETE ${email}?\n\nThis will remove their account, all bots, and all trade history. This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/delete-user?email=${encodeURIComponent(email)}`);
      toast.success(`${email} permanently deleted`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const updateModTierFn = async (email, newTier) => {
    try {
      await api.post(`/admin/update-mod-tier?email=${encodeURIComponent(email)}&tier=${newTier}`);
      toast.success(`${email} updated to ${newTier} tier`);
      loadData();
    } catch (error) {
      toast.error('Failed to update tier');
    }
  };

  const generateModCode = async () => {
    setGeneratingCode(true);
    try {
      const response = await api.post(`/admin/generate-mod-code?tier=${codeTier}`);
      toast.success(`Code generated: ${response.data.code} (${codeTier} tier)`);
      loadData();
    } catch (error) {
      toast.error('Failed to generate code');
    } finally {
      setGeneratingCode(false);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success('Code copied to clipboard');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const deleteModCode = async (code) => {
    if (!window.confirm(`Delete mod code ${code}?`)) return;
    try {
      await api.delete(`/admin/mod-codes/${code}`);
      toast.success('Code deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete code');
    }
  };

  const tierBadge = (tier) => {
    const config = { core: 'bg-green-500', pro: 'bg-blue-500', elite: 'bg-yellow-500', full: 'bg-purple-500', owner: 'bg-purple-500', mod: 'bg-cyan-500', none: 'bg-gray-500' };
    return <Badge className={`${config[tier] || config.none} text-white text-xs`}>{tier === 'full' ? 'FULL ACCESS' : tier?.toUpperCase()}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0D12] text-[#F5F7FA] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <p className="text-[#94A3B8]">Verifying admin access...</p>
        </div>
      </div>
    );
  }
  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-[#0B0D12] text-[#F5F7FA]">
      {/* Header */}
      <header className="glass-effect border-b border-purple-500/30 sticky top-0 z-50">
        <div className="container mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button onClick={() => navigate('/dashboard')} variant="ghost" className="text-[#94A3B8] hover:text-[#F5F7FA]">
              <ArrowLeft className="w-4 h-4 mr-2" /> Dashboard
            </Button>
            <Badge className="bg-purple-500 text-white">
              <Shield className="w-3 h-3 mr-1" /> Admin Panel
            </Badge>
          </div>
          <Button onClick={loadData} variant="outline" size="sm" className="border-purple-500/50 text-purple-400">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>
      </header>

      <div className="container mx-auto px-4 md:px-8 py-8">
        {/* Stats */}
        <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          {[
            { icon: <Users className="w-7 h-7 text-purple-400" />, label: 'Total Users', value: stats?.total_users || 0 },
            { icon: <TrendingUp className="w-7 h-7 text-green-400" />, label: 'Active Subs', value: stats?.active_subscriptions || 0 },
            { icon: <Bot className="w-7 h-7 text-blue-400" />, label: 'Total Bots', value: stats?.total_bots || 0 },
            { icon: <Bot className="w-7 h-7 text-green-400" />, label: 'Active Bots', value: stats?.active_bots || 0 },
            { icon: <TrendingUp className="w-7 h-7 text-[#C9A24D]" />, label: 'Total Trades', value: stats?.total_trades || 0 },
          ].map((s, i) => (
            <Card key={i} className="bg-[#15171C] border-[#1E293B]">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  {s.icon}
                  <div><p className="text-2xl font-bold">{s.value}</p><p className="text-xs text-[#94A3B8]">{s.label}</p></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </motion.div>

        {/* Subscription Breakdown */}
        <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.1 }} className="mb-8">
          <Card className="bg-[#15171C] border-[#1E293B]">
            <CardHeader><CardTitle className="font-['Outfit']">Subscription Breakdown</CardTitle></CardHeader>
            <CardContent>
              <div className="flex gap-4 flex-wrap">
                {[
                  { tier: 'owner', count: stats?.tier_breakdown?.owner },
                  { tier: 'elite', count: stats?.tier_breakdown?.elite },
                  { tier: 'pro', count: stats?.tier_breakdown?.pro },
                  { tier: 'core', count: stats?.tier_breakdown?.core },
                  { tier: 'none', count: stats?.tier_breakdown?.none },
                ].map((s, i) => (
                  <div key={i} className="flex items-center gap-2">{tierBadge(s.tier)}<span>{s.count || 0}</span></div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Test Bot Folder */}
        <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.12 }} className="mb-8">
          <TestBotFolder />
        </motion.div>

        {/* User Activity Dashboard */}
        <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.15 }} className="mb-8">
          <Card className="bg-[#15171C] border-[#1E293B]">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <Activity className="w-5 h-5 text-emerald-400" />
                  <div>
                    <CardTitle className="font-['Outfit']">User Activity Dashboard</CardTitle>
                    <p className="text-sm text-[#94A3B8] mt-0.5">{userActivity.length} users tracked</p>
                  </div>
                </div>
                <div className="flex gap-2 items-center">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
                    <Input
                      data-testid="activity-search-input"
                      placeholder="Search users..."
                      value={activitySearch}
                      onChange={(e) => setActivitySearch(e.target.value)}
                      className="bg-[#0B0D12] border-[#1E293B] pl-9 w-48"
                    />
                  </div>
                  <div className="flex gap-1">
                    {[
                      { key: 'last_login', label: 'Last Login' },
                      { key: 'total_trades', label: 'Trades' },
                      { key: 'total_pnl', label: 'P&L' },
                      { key: 'created_at', label: 'Newest' },
                    ].map(s => (
                      <button
                        key={s.key}
                        data-testid={`sort-${s.key}`}
                        onClick={() => { setActivitySort(s.key); }}
                        className={`px-2.5 py-1.5 rounded text-xs font-medium transition-all border ${
                          activitySort === s.key
                            ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50'
                            : 'bg-transparent border-[#1E293B] text-[#94A3B8] hover:border-emerald-500/30'
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={loadUserActivity}
                    disabled={loadingActivity}
                    className="border-emerald-500/50 text-emerald-400"
                    data-testid="refresh-activity-btn"
                  >
                    {loadingActivity ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {userActivity.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="user-activity-table">
                    <thead>
                      <tr className="border-b border-[#1E293B]">
                        <th className="text-left py-3 px-3 text-[#94A3B8] font-medium text-xs">User</th>
                        <th className="text-left py-3 px-3 text-[#94A3B8] font-medium text-xs">Tier</th>
                        <th className="text-center py-3 px-3 text-[#94A3B8] font-medium text-xs">Bots</th>
                        <th className="text-center py-3 px-3 text-[#94A3B8] font-medium text-xs">Trades</th>
                        <th className="text-right py-3 px-3 text-[#94A3B8] font-medium text-xs">P&L</th>
                        <th className="text-center py-3 px-3 text-[#94A3B8] font-medium text-xs">Win Rate</th>
                        <th className="text-center py-3 px-3 text-[#94A3B8] font-medium text-xs">Exchange</th>
                        <th className="text-right py-3 px-3 text-[#94A3B8] font-medium text-xs">Last Login</th>
                        <th className="text-right py-3 px-3 text-[#94A3B8] font-medium text-xs">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userActivity
                        .filter(u => {
                          if (!activitySearch) return true;
                          const q = activitySearch.toLowerCase();
                          return u.email?.toLowerCase().includes(q) || u.full_name?.toLowerCase().includes(q);
                        })
                        .sort((a, b) => {
                          if (activitySort === 'last_login') return (b.last_login || '').localeCompare(a.last_login || '');
                          if (activitySort === 'total_trades') return (b.total_trades || 0) - (a.total_trades || 0);
                          if (activitySort === 'total_pnl') return (b.total_pnl || 0) - (a.total_pnl || 0);
                          if (activitySort === 'created_at') return (b.created_at || '').localeCompare(a.created_at || '');
                          return 0;
                        })
                        .map((u) => (
                        <tr key={u.id} className="border-b border-[#1E293B]/50 hover:bg-[#1E293B]/20 transition-colors" data-testid={`user-row-${u.email}`}>
                          <td className="py-3 px-3">
                            <div className="flex items-center gap-2.5">
                              <div className="w-8 h-8 rounded-full bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
                                <span className="text-emerald-400 text-xs font-bold">{(u.full_name || u.email)?.[0]?.toUpperCase()}</span>
                              </div>
                              <div className="min-w-0">
                                <p className="font-medium text-sm truncate">{u.full_name || 'N/A'}</p>
                                <p className="text-xs text-[#64748B] truncate">{u.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-3">
                            {tierBadge(u.subscription_tier === 'mod' ? (u.mod_tier || 'mod') : u.subscription_tier)}
                          </td>
                          <td className="py-3 px-3 text-center">
                            <span className="font-['JetBrains_Mono'] text-sm">
                              <span className={u.active_bots > 0 ? 'text-green-400' : 'text-[#94A3B8]'}>{u.active_bots}</span>
                              <span className="text-[#64748B]">/{u.total_bots}</span>
                            </span>
                          </td>
                          <td className="py-3 px-3 text-center">
                            <span className="font-['JetBrains_Mono'] text-sm text-[#F5F7FA]">{u.total_trades}</span>
                          </td>
                          <td className="py-3 px-3 text-right">
                            <span className={`font-['JetBrains_Mono'] text-sm font-medium ${u.total_pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {u.total_pnl >= 0 ? '+' : ''}{'\u00A3'}{u.total_pnl.toFixed(2)}
                            </span>
                          </td>
                          <td className="py-3 px-3 text-center">
                            <span className={`font-['JetBrains_Mono'] text-sm ${u.win_rate >= 50 ? 'text-green-400' : u.win_rate > 0 ? 'text-yellow-400' : 'text-[#94A3B8]'}`}>
                              {u.win_rate.toFixed(1)}%
                            </span>
                          </td>
                          <td className="py-3 px-3 text-center">
                            {u.exchange_connected ? (
                              <Wifi className="w-4 h-4 text-green-400 mx-auto" />
                            ) : (
                              <WifiOff className="w-4 h-4 text-[#64748B] mx-auto" />
                            )}
                          </td>
                          <td className="py-3 px-3 text-right">
                            {u.last_login ? (
                              <div className="flex items-center justify-end gap-1.5">
                                <Clock className="w-3 h-3 text-[#64748B]" />
                                <span className="text-xs text-[#94A3B8]">{new Date(u.last_login).toLocaleDateString()}</span>
                              </div>
                            ) : (
                              <span className="text-xs text-[#64748B]">Never</span>
                            )}
                          </td>
                          <td className="py-3 px-3 text-right">
                            {u.subscription_tier !== 'owner' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); deleteUser(u.email); }}
                                className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-7 px-2"
                                data-testid={`delete-user-${u.email}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 text-[#1E293B] mx-auto mb-3" />
                  <p className="text-[#94A3B8]">No user activity data</p>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Set as Moderator */}
        <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2 }} className="mb-8">
          <Card className="bg-gradient-to-r from-cyan-500/20 to-cyan-500/5 border-cyan-500/50">
            <CardContent className="pt-6 pb-6">
              <div className="flex items-center gap-4 mb-4">
                <UserPlus className="w-8 h-8 text-cyan-400" />
                <div>
                  <h3 className="font-semibold" data-testid="set-mod-title">Set as Moderator</h3>
                  <p className="text-sm text-[#94A3B8]">Grant trading access at a specific tier without subscription</p>
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Input
                  data-testid="mod-email-input"
                  placeholder="Enter user email..."
                  value={modEmail}
                  onChange={(e) => setModEmail(e.target.value)}
                  className="bg-[#0B0D12] border-cyan-500/30 flex-1 min-w-[200px]"
                />
                <div className="flex gap-1">
                  {TIER_OPTIONS.map(t => (
                    <button
                      key={t.value}
                      data-testid={`mod-tier-${t.value}`}
                      onClick={() => setModTier(t.value)}
                      className={`px-3 py-2 rounded text-xs font-semibold transition-all border ${
                        modTier === t.value
                          ? `${t.color} text-white border-transparent`
                          : 'bg-transparent border-[#1E293B] text-[#94A3B8] hover:border-cyan-500/50'
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
                <Button data-testid="make-mod-btn" onClick={makeMod} className="bg-cyan-500 hover:bg-cyan-600 text-black font-semibold">
                  <UserPlus className="w-4 h-4 mr-2" /> Make Mod
                </Button>
              </div>
              <p className="text-xs text-[#94A3B8] mt-2">Selected tier: <span className="text-cyan-400 font-medium">{modTier.toUpperCase()}</span> — {TIER_OPTIONS.find(t => t.value === modTier)?.desc}</p>
            </CardContent>
          </Card>
        </motion.div>

        {/* Mod Code Generator */}
        <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.3 }} className="mb-8">
          <Card className="bg-[#15171C] border-[#1E293B]">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <Key className="w-5 h-5 text-cyan-400" />
                  <CardTitle className="font-['Outfit']">Moderator Codes</CardTitle>
                </div>
                <div className="flex gap-2 items-center">
                  <div className="flex gap-1">
                    {TIER_OPTIONS.map(t => (
                      <button
                        key={t.value}
                        onClick={() => setCodeTier(t.value)}
                        className={`px-2.5 py-1.5 rounded text-xs font-semibold transition-all border ${
                          codeTier === t.value
                            ? `${t.color} text-white border-transparent`
                            : 'bg-transparent border-[#1E293B] text-[#94A3B8] hover:border-cyan-500/50'
                        }`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                  <Button data-testid="generate-code-btn" onClick={generateModCode} disabled={generatingCode} className="bg-cyan-500 hover:bg-cyan-600 text-black font-semibold">
                    {generatingCode ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Key className="w-4 h-4 mr-2" />}
                    Generate Code
                  </Button>
                </div>
              </div>
              <p className="text-sm text-[#94A3B8] mt-1">Unique codes for mods. They use the code as their password when signing up.</p>
            </CardHeader>
            <CardContent>
              {modCodes.length > 0 ? (
                <div className="space-y-2">
                  {modCodes.map((mc, idx) => (
                    <div key={idx} className={`flex items-center justify-between p-3 rounded-lg border ${mc.used ? 'bg-[#0B0D12]/50 border-[#1E293B]/50' : 'bg-cyan-500/5 border-cyan-500/30'}`}>
                      <div className="flex items-center gap-3">
                        <code className={`font-['JetBrains_Mono'] text-lg font-bold tracking-wider ${mc.used ? 'text-[#64748B]' : 'text-cyan-400'}`} data-testid={`mod-code-${idx}`}>
                          {mc.code}
                        </code>
                        {tierBadge(mc.assigned_tier || mc.tier || 'core')}
                        {mc.used ? (
                          <Badge className="bg-gray-600 text-xs">Used by {mc.used_by}</Badge>
                        ) : (
                          <Badge className="bg-cyan-500/20 text-cyan-400 text-xs">Available</Badge>
                        )}
                      </div>
                      {!mc.used && (
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => copyCode(mc.code)} className="text-cyan-400 hover:text-cyan-300">
                            {copiedCode === mc.code ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => deleteModCode(mc.code)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10" data-testid={`delete-code-${mc.code}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[#94A3B8] text-sm py-4 text-center">No mod codes generated yet.</p>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Active Moderators */}
        <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.4 }} className="mb-8">
          <Card className="bg-[#15171C] border-[#1E293B]">
            <CardHeader><CardTitle className="font-['Outfit']">Active Moderators ({mods.length})</CardTitle></CardHeader>
            <CardContent>
              {mods.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#1E293B]">
                        <th className="text-left py-3 px-4 text-[#94A3B8] font-medium">User</th>
                        <th className="text-left py-3 px-4 text-[#94A3B8] font-medium">Assigned Tier</th>
                        <th className="text-left py-3 px-4 text-[#94A3B8] font-medium">Joined</th>
                        <th className="text-right py-3 px-4 text-[#94A3B8] font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mods.map((mod) => (
                        <tr key={mod.id} className="border-b border-[#1E293B]/50 hover:bg-[#1E293B]/20">
                          <td className="py-3 px-4">
                            <p className="font-medium">{mod.full_name}</p>
                            <p className="text-sm text-[#94A3B8]">{mod.email}</p>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex gap-1">
                              {TIER_OPTIONS.map(t => (
                                <button
                                  key={t.value}
                                  onClick={() => updateModTierFn(mod.email, t.value)}
                                  className={`px-2 py-1 rounded text-xs font-semibold transition-all border ${
                                    (mod.mod_tier || 'elite') === t.value
                                      ? `${t.color} text-white border-transparent`
                                      : 'bg-transparent border-[#1E293B] text-[#64748B] hover:border-cyan-500/50'
                                  }`}
                                >
                                  {t.label}
                                </button>
                              ))}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-[#94A3B8]">{new Date(mod.created_at).toLocaleDateString()}</td>
                          <td className="py-3 px-4">
                            <div className="flex justify-end gap-2">
                              <Button variant="ghost" size="sm" onClick={() => revokeMod(mod.email)} className="text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/10">
                                <Trash2 className="w-4 h-4 mr-1" /> Revoke
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => deleteUser(mod.email)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                                <Trash2 className="w-4 h-4 mr-1" /> Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-[#94A3B8] text-sm py-4 text-center">No moderators yet.</p>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Mod Activity Dashboard */}
        <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.5 }}>
          <Card className="bg-[#15171C] border-[#1E293B]">
            <CardHeader>
              <div className="flex items-center gap-3">
                <Activity className="w-5 h-5 text-cyan-400" />
                <CardTitle className="font-['Outfit']">Mod Activity Dashboard</CardTitle>
              </div>
              <p className="text-sm text-[#94A3B8]">Trading performance and activity for all moderators</p>
            </CardHeader>
            <CardContent>
              {modActivity.length > 0 ? (
                <div className="space-y-4">
                  {modActivity.map((mod) => (
                    <div key={mod.id} className="rounded-lg border border-[#1E293B] overflow-hidden">
                      {/* Mod Summary Row */}
                      <button
                        onClick={() => setExpandedMod(expandedMod === mod.id ? null : mod.id)}
                        className="w-full flex items-center justify-between p-4 hover:bg-[#1E293B]/20 transition-colors"
                        data-testid={`mod-activity-${mod.email}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center">
                            <span className="text-cyan-400 font-bold">{(mod.full_name || mod.email)[0]?.toUpperCase()}</span>
                          </div>
                          <div className="text-left">
                            <p className="font-medium">{mod.full_name || mod.email}</p>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-[#94A3B8]">{mod.email}</span>
                              {tierBadge(mod.mod_tier || 'elite')}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="text-right">
                            <p className="text-xs text-[#94A3B8]">Bots</p>
                            <p className="font-bold">{mod.active_bots}/{mod.total_bots}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-[#94A3B8]">Trades</p>
                            <p className="font-bold">{mod.total_trades}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-[#94A3B8]">P&L</p>
                            <p className={`font-bold font-['JetBrains_Mono'] ${mod.total_pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {mod.total_pnl >= 0 ? '+' : ''}£{mod.total_pnl.toFixed(2)}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-[#94A3B8]">Win Rate</p>
                            <p className="font-bold">{mod.avg_win_rate.toFixed(1)}%</p>
                          </div>
                          {expandedMod === mod.id ? <ChevronUp className="w-4 h-4 text-[#94A3B8]" /> : <ChevronDown className="w-4 h-4 text-[#94A3B8]" />}
                        </div>
                      </button>

                      {/* Expanded Bot Details */}
                      {expandedMod === mod.id && (
                        <div className="border-t border-[#1E293B] bg-[#0B0D12]/50 p-4">
                          {mod.bots && mod.bots.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                              {mod.bots.map((bot) => (
                                <div key={bot.id} className="p-3 rounded-lg border border-[#1E293B] bg-[#15171C]">
                                  <div className="flex items-center justify-between mb-2">
                                    <p className="font-medium text-sm">{bot.strategy?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                                    <Badge className={`text-xs ${bot.status === 'active' ? 'bg-green-500' : bot.status === 'paused' ? 'bg-yellow-500' : 'bg-gray-500'}`}>
                                      {bot.status?.toUpperCase()}
                                    </Badge>
                                  </div>
                                  <div className="grid grid-cols-3 gap-2 text-xs">
                                    <div>
                                      <p className="text-[#94A3B8]">Balance</p>
                                      <p className="font-['JetBrains_Mono'] font-semibold">£{(bot.paper_balance || 0).toFixed(0)}</p>
                                    </div>
                                    <div>
                                      <p className="text-[#94A3B8]">P&L</p>
                                      <p className={`font-['JetBrains_Mono'] font-semibold ${(bot.paper_pnl || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                        £{(bot.paper_pnl || 0).toFixed(2)}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-[#94A3B8]">Win Rate</p>
                                      <p className="font-semibold">{(bot.win_rate || 0).toFixed(1)}%</p>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-[#94A3B8] text-sm text-center py-2">No bots created yet</p>
                          )}
                          {mod.last_trade && (
                            <p className="text-xs text-[#64748B] mt-3">Last trade: {new Date(mod.last_trade).toLocaleString()}</p>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Activity className="w-12 h-12 text-[#1E293B] mx-auto mb-3" />
                  <p className="text-[#94A3B8]">No moderator activity yet</p>
                  <p className="text-xs text-[#64748B] mt-1">Activity will appear once moderators start trading</p>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminPanel;
